import { useRef, useState } from "react";
import { withFeatureSafe } from 'Common/ErrorBoundaries';
import CustomInput from "Common/CustomInput/CustomInput";
import ArrowIcon from "SVGS/ArrowIcon";
import { motion, AnimatePresence } from "framer-motion";
import HoverRevealScrollbarWrapper from "Components/HoverRevealScrollbarWrapper/HoverRevealScrollbarWrapper";
import { Tooltip } from 'react-tooltip'
import * as Popover from "@radix-ui/react-popover";


const SearchableDropdown = ({
    options,
    onChange,
    value,
    isOpen,
    setIsOpen,
    placeholder = "Search or select...",
    widthClass = "w-[350px]",
    containerClassName = "",
    controlClassName = "",
    menuClassName = "",
    menuWidthClass = "",
    textClassName = "",
    tooltipId = "practiceName_tooltip",
    triggerVariant = "default",
    avatarText = "",
    subtitleText = "",
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownDiv = useRef(null);
    const inputRef = useRef(null);

    const [selectedRowNumber, setSelectedRowNumber] = useState(0);

    const filteredOptions = options.filter(option =>
        option.label.split("-")[1].toLowerCase().includes(searchQuery.toLowerCase())
    );
    const onKeyDownPressed = async (e) => {
        if (e.keyCode === 13) {
            if (filteredOptions.length > 0) {
                setSelectedRowNumber(0)
                onChange(filteredOptions[selectedRowNumber].id);
                setIsOpen(false);
                setSearchQuery("");
            }
        } else if (e.keyCode === 38) {
            // if user presses the UP
            if (selectedRowNumber > 0) {
                setSelectedRowNumber((prevSelectedRow) => prevSelectedRow - 1);
                const selectedRowUP = selectedRowNumber - 1
                const elem = document.getElementById(`SearchableDropdown_${selectedRowUP}`);
                elem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        } else if (e.keyCode === 40) {
            // IF USER PRESSES DOWN KEY
            if (selectedRowNumber < filteredOptions.length - 1) {
                setSelectedRowNumber((prevSelectedRow) => prevSelectedRow + 1);
                const selectedRowDown = selectedRowNumber + 1
                const elem = document.getElementById(`SearchableDropdown_${selectedRowDown}`);
                elem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
            }
        } else if (e.keyCode === 27) {
            // IF USER PRESSES ESC KEY THEN RESET THE COMPONENT TO ITS ORIGINAL STATE
            setIsOpen(false);
            setSearchQuery("");
        }
    };

    const handleOnchange = (e) => {
        setSearchQuery(e.target.value)
        setSelectedRowNumber(0)
    }

    const handleClick = (item) => {
        onChange(item.id);
        setIsOpen(false);
        setSearchQuery("");
    };
    const handleOpenChange = (open) => {
        setIsOpen(open);
        if (open) {
            setSearchQuery("");
            setSelectedRowNumber(0);
            return;
        }
        setSearchQuery("");
    };

    const selectedOptionLabel = options.find(option => option.id === value)?.label || "Search or select";
    const selectedMainText = selectedOptionLabel.split("-")[1] || selectedOptionLabel;

    return (
        <div className={`relative z-10 w-fit ${containerClassName}`} data-tooltip-id={tooltipId} data-tooltip-content={selectedMainText} ref={dropdownDiv}>
            <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
                <Popover.Trigger asChild>
                    <div
                        className={`relative ${widthClass} px-[0.4rem] py-[0.3rem] text-base rounded text-gray-900 flex justify-between items-center cursor-pointer   ${controlClassName}`}>
                        {triggerVariant === "practice" ? (
                            <div className="flex items-center w-full gap-[9px]">
                                <div className="w-10 h-10 rounded-[8px] overflow-hidden flex items-center justify-center flex-shrink-0 bg-white">
                                    <img
                                        src="/trucare-favicon.png"
                                        alt="Trucare"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className={`text-[12px] leading-[1.3] truncate ${textClassName}`} style={{ color: "rgb(21, 26, 32)", fontWeight: 500 }}>{selectedMainText}</div>
                                    <div className="text-[10.5px] leading-[1.3] text-[#889AB3] truncate">{subtitleText}</div>
                                </div>
                                <ArrowIcon
                                    size={14}
                                    className={`text-[#889AB3] flex-shrink-0 transform transition-transform duration-200 ${isOpen && "rotate-180"}`}
                                />
                            </div>
                        ) : (
                            <>
                                <span className={`overflow-hidden whitespace-nowrap text-ellipsis ${textClassName}`}>{selectedMainText}</span>
                                <ArrowIcon
                                    size={15}
                                    className={`transform transition-transform duration-200 ${isOpen && "rotate-180"}`}
                                />
                            </>
                        )}
                        <Tooltip hidden={isOpen} id={tooltipId} style={{ fontSize: "10px", zIndex: 60 }} />
                    </div>
                </Popover.Trigger>
                <AnimatePresence>
                    {isOpen && (
                        <Popover.Portal>
                            <Popover.Content
                                side="bottom"
                                align="start"
                                sideOffset={4}
                                collisionPadding={8}
                                onOpenAutoFocus={(e) => {
                                    e.preventDefault();
                                    inputRef.current?.focus();
                                }}
                                className="z-[1000]"
                            >
                                <motion.div
                                    className={`text-base ${menuWidthClass || ""} rounded-lg bg-white opacity-100 shadow-lg overflow-hidden ${menuClassName}`}
                                    style={{ width: menuWidthClass ? undefined : "var(--radix-popover-trigger-width)" }}
                                    initial={{ opacity: 0, scaleY: 1, transformOrigin: "top" }}
                                    animate={{ opacity: 1, scaleY: 1 }}
                                    exit={{ opacity: 0, scaleY: 0 }}
                                    transition={{ duration: 0.2, ease: "easeOut" }}
                                >
                                    <div className="p-2">
                                        <CustomInput
                                            ref={inputRef}
                                            autoFocus
                                            placeholder={placeholder}
                                            value={searchQuery}
                                            onChange={handleOnchange}
                                            className="border-t-0 border-l-0 border-r-0 focus:border-gray-200"
                                            onKeyDown={onKeyDownPressed}
                                        />
                                    </div>
                                    <HoverRevealScrollbarWrapper size={6}>
                                        <motion.ul
                                            data-hover-scrollable="true"
                                            className="overflow-y-auto transition-all opacity-100 max-h-72 bg-white"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                        >
                                            {filteredOptions.length === 0 ? (
                                                <li className="p-2 text-gray-500">No results found</li>
                                            ) : (
                                                filteredOptions.map((item, index) => {
                                                    let selected = false;
                                                    if (index === selectedRowNumber) {
                                                        selected = true;
                                                    }
                                                    return (
                                                        <li
                                                            id={`SearchableDropdown_${index}`}
                                                            key={item.id}
                                                            className={`p-2 cursor-pointer bg-white hover:bg-gray-100 whitespace-nowrap ${selected ? "bg-[#F3F4F6]" : ""} ${value === item.id ? "bg-[#F3F4F6] text-black hover:bg-[#F3F4F6]" : ""}`}

                                                            onClick={() => handleClick(item)}
                                                        >
                                                            {item.label.split("-")[1]}
                                                        </li>
                                                    )
                                                })
                                            )}
                                        </motion.ul>
                                    </HoverRevealScrollbarWrapper>
                                </motion.div>
                            </Popover.Content>
                        </Popover.Portal>
                    )}
                </AnimatePresence>
            </Popover.Root>
        </div>
    );
};

export default withFeatureSafe(SearchableDropdown);